import {
  TRANSCRIPT_INDEX_JUMP_EVENT,
  type TranscriptIndexJumpDetail,
} from '@src/utility/transcriptEvents';
import { useCallback, useEffect, useRef } from 'react';

import {
  LANDING_FLASH_ATTR,
  LANDING_FLASH_MS,
} from '../reelStructure/ReelStructureHeadings';
import { useReelTranscriptStructure } from '../reelStructure/ReelTranscriptStructureContext';
import {
  resolveReelWordSection,
  revealCollapsedWord,
} from '../reelStructure/revealCollapsedWord';
import { scrollCursorIntoJumpView } from '../utils';

import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { MutableRefObject, RefObject } from 'react';

type Cursor = { from: number; to: number };

/**
 * Only what a rendered transcript token is read for here: the id lookup that
 * resolves the target, and the `initial_index` the scroll's own scan falls back
 * to when a word cannot be found by identity.
 */
type TokenLike = {
  id?: string;
  source_uuid?: string;
  state?: string;
  initial_index: number;
};

type Options = {
  /** The surface's own creator — also the scope token incoming events carry. */
  videoCreator: ITranscriptVideoCreator;
  /**
   * The elements this transcript is actually rendering — the array an incoming
   * `tokenId` is resolved against, which is not necessarily the array the caller
   * counted its index in.
   */
  transcriptElements: readonly TokenLike[] | undefined;
  skipToWordWithIndex: (
    index: number,
    setCursor: (cursor: Cursor) => void,
    setAutoScroll: (autoScroll: boolean) => void,
  ) => void;
  setCursor: (cursor: Cursor) => void;
  setAutoScroll: (autoScroll: boolean) => void;
  /**
   * The transcript's own scroll container. Passed rather than looked up: two
   * transcripts coexist with the clip preview and both carry
   * `#transcript-scrollable-container`, so a global lookup scrolls the wrong
   * one (`docs/transcript-ui-patterns.md`).
   */
  scrollableContainerRef: RefObject<HTMLDivElement | null>;
  /** Cleared once this jump has placed the view, as the playback path does. */
  allowInitialScrollRef: MutableRefObject<boolean>;
  setLastTimeUpdated: (time: number) => void;
};

/**
 * Where the target sits in *this* transcript's array.
 *
 * The id wins when it resolves, because the caller counted its index in
 * `getFullTranscript()` while what is rendered here is a projection of it —
 * doubled whitespace deduped away, or the karaoke view's flattening. A struck
 * token is not a landing place, so removed/cut matches are passed over and the
 * index stands.
 */
function resolveIndex(
  detail: TranscriptIndexJumpDetail,
  elements: readonly TokenLike[] | undefined,
): number {
  if (!detail.tokenId || !elements?.length) return detail.index;
  const found = elements.findIndex(
    el =>
      (el.source_uuid ?? el.id) === detail.tokenId &&
      el.state !== 'removed' &&
      el.state !== 'cut',
  );
  return found === -1 ? detail.index : found;
}

/**
 * The row currently flashing, and the timer that will end it. Keyed weakly so
 * a row that leaves the DOM takes its entry with it.
 */
const landingFlashTimers = new WeakMap<Element, number>();

/**
 * The landing flash — the Reel Editor's arrival token, so both ends of the
 * round trip mark the row the same way.
 *
 * An attribute the row's own CSS animates, NOT `element.animate()`: WAAPI
 * keyframes do not substitute `var()`, so the design token a hand-rolled
 * animation passes it is dropped as an invalid value and nothing paints.
 *
 * Re-arming on a row that is already flashing restarts it, which is what a
 * second jump to the same Section should look like — and the previous timer is
 * cancelled first, or it fires mid-way through the new flash and cuts it off
 * after whatever was left of the old one.
 *
 * Exported for its test only: the rest of the hook needs a rendered transcript
 * to exercise, and the part worth pinning here is this attribute contract with
 * `SectionRow`'s CSS.
 */
export function flashLanding(row: Element): void {
  const pending = landingFlashTimers.get(row);
  if (pending !== undefined) window.clearTimeout(pending);

  row.removeAttribute(LANDING_FLASH_ATTR);
  // Read back a layout value so the removal and the re-add land in separate
  // style recalculations — otherwise the browser coalesces them and the
  // animation never restarts.
  void (row as HTMLElement).offsetWidth;
  row.setAttribute(LANDING_FLASH_ATTR, 'true');

  landingFlashTimers.set(
    row,
    window.setTimeout(() => {
      landingFlashTimers.delete(row);
      row.removeAttribute(LANDING_FLASH_ATTR);
    }, LANDING_FLASH_MS),
  );
}

/**
 * "Land the cursor on this element and bring it into view" — the transcript's
 * one jump, reached by any external caller that asks for it through
 * {@link TRANSCRIPT_INDEX_JUMP_EVENT} (PLAT-11061). Hosts mount it for that
 * subscription alone; the returned `jumpToIndex` has no in-transcript caller
 * since PLAT-11039 removed the highlight prev/next buttons.
 *
 * Both halves are needed, and in this order. The cursor marker is the indicator
 * that you landed, so it reads even when the target was already on-screen — and
 * a scroll on its own does not survive playback: the scroll anchors on the
 * cursor, so while playing the per-tick auto-scroll effect pulls the view
 * straight back to the real playhead. A cursor-less jump would undo itself
 * inside a frame, and only while playing.
 *
 * The scroll is `scrollCursorIntoJumpView`, not `Transcript`'s
 * `scrollToPlayhead`: playback anchors the words about to be spoken near the
 * top and counts "anywhere in the container" as placed, which lands a jump on
 * the panel's last line. Both cover `data-index` and `data-initial-index`, and
 * neither calls `scrollIntoView` or moves focus, so a jump cannot pull focus off
 * the renderer canvas and revert an in-flight edit (PLAT-10700).
 *
 * Moves the cursor; creates no text selection. A selection would arm every
 * selection-driven action (remove, save as clip, add overlay) over a range the
 * creator never chose.
 */
export function useTranscriptIndexJump({
  videoCreator,
  transcriptElements,
  skipToWordWithIndex,
  setCursor,
  setAutoScroll,
  scrollableContainerRef,
  allowInitialScrollRef,
  setLastTimeUpdated,
}: Options): (index: number) => void {
  // Mirrored, not a dependency: the rendered elements are a fresh array on
  // every render (CLAUDE.md pitfall 18), so depending on them would re-register
  // the listener each time.
  const elementsRef = useRef(transcriptElements);
  useEffect(() => {
    elementsRef.current = transcriptElements;
  }, [transcriptElements]);

  // PLAT-11159 — the clip preview publishes the reel's structure + collapse
  // through context (null on every other host). Mirrored through a ref for
  // the same reason as the elements: `jumpToIndex` must stay stable.
  const reelView = useReelTranscriptStructure();
  const reelViewRef = useRef(reelView);
  useEffect(() => {
    reelViewRef.current = reelView;
  }, [reelView]);

  const jumpToIndex = useCallback(
    (index: number) => {
      setLastTimeUpdated(0);
      const reelView = reelViewRef.current;
      const elements = elementsRef.current?.slice();
      // Stamped ONCE for both readers below. The stamp re-derives the reel's
      // Sections from the timeline and walks every token, so the two questions
      // a jump asks of it — "is this word folded away?" and "which Section's
      // heading do I land on?" — share one answer rather than paying for it
      // twice.
      const stamp = reelView ? reelView.stampWords(elements) : undefined;
      // PLAT-11159 — expand-then-look: a target inside a folded passage has
      // no DOM row to land on, so whatever hides it is opened FIRST. The
      // reveal is a React state write, so when it happens the row cannot
      // exist until after the next commit — the lookup below gets a longer
      // retry leash instead of one frame.
      const revealed = revealCollapsedWord(
        reelView,
        elements,
        index,
        stamp,
        'jump',
      );
      skipToWordWithIndex(index, setCursor, setAutoScroll);
      const container = scrollableContainerRef.current;
      if (!container) return;
      allowInitialScrollRef.current = false;

      // PLAT-11163 — with headings on the page, the jump lands on the
      // Section's heading, not a bare word: the heading is what confirms the
      // creator arrived at the right passage. Falls back to the word when no
      // heading resolves (non-reel, or a Section with no heading).
      const sectionId = resolveReelWordSection(
        reelView,
        elements,
        index,
        stamp,
      )?.sectionId;
      const landOnTarget = (scope: HTMLElement): boolean => {
        if (sectionId !== undefined) {
          const heading = scope.querySelector(
            `[data-reel-section-heading="${sectionId}"]`,
          );
          if (heading) {
            heading.scrollIntoView?.({ block: 'center' });
            flashLanding(heading);
            return true;
          }
        }
        return scrollCursorIntoJumpView(scope, index, elements);
      };
      if (!revealed && landOnTarget(container)) {
        return;
      }

      // The row can be a frame late: the tab flip that brings this transcript
      // out of `display: none` lands in the same commit as the jump, and a row
      // the cursor move only just brought into the list has not been laid out
      // yet. One retry after the next frame — the try-then-rAF-retry the
      // playback path uses for the same reason (CLAUDE.md pitfall 16) — and
      // then the view is left where it is rather than scrolled somewhere
      // guessed at. A jump that just opened a folded passage waits out a few
      // more frames: the expansion has to render before the row can be found.
      const retry = (attemptsLeft: number) => {
        requestAnimationFrame(() => {
          const retryContainer = scrollableContainerRef.current;
          if (!retryContainer) return;
          if (landOnTarget(retryContainer)) return;
          if (attemptsLeft > 0) retry(attemptsLeft - 1);
        });
      };
      retry(revealed ? 5 : 0);
    },
    [
      setLastTimeUpdated,
      skipToWordWithIndex,
      setCursor,
      setAutoScroll,
      scrollableContainerRef,
      allowInitialScrollRef,
    ],
  );

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TranscriptIndexJumpDetail>).detail;
      if (!detail) return;
      // Identity, as `TranscriptVideoPlayer` reads a scoped seek: an event for
      // the clip preview's transcript must not move Creator Studio's.
      if (detail.creator && detail.creator !== videoCreator) return;
      jumpToIndex(resolveIndex(detail, elementsRef.current));
    };
    window.addEventListener(TRANSCRIPT_INDEX_JUMP_EVENT, handler);
    return () => {
      window.removeEventListener(TRANSCRIPT_INDEX_JUMP_EVENT, handler);
    };
  }, [jumpToIndex, videoCreator]);

  return jumpToIndex;
}
